import React, { useRef, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { stripAllHtmlAndEncodedTags } from "../../utils";

const modules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};

const formats = ["bold", "italic", "underline", "list", "bullet"];

const TextEditor = ({ inputData, onChange, disabled, name }: any) => {
  const quillRef = useRef<ReactQuill>(null);
  const isMounted = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed initial value once on mount.
  // setTimeout(0) defers one tick so Quill's clipboard module is fully
  // initialised before we call dangerouslyPasteHTML — without this deferral
  // Quill can insert the raw HTML string as plain text.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (quillRef.current && inputData) {
        const editor = quillRef.current.getEditor();
        editor.clipboard.dangerouslyPasteHTML(0, inputData);
        editor.setSelection(editor.getLength(), 0);
      }
      isMounted.current = true;
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Clear editor when inputData becomes empty (add mode / form reset)
  useEffect(() => {
    if (!isMounted.current) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    if (!inputData || inputData === "") {
      editor.setText("");        // clears content
    }
  }, [inputData]);

  const handleChange = useCallback(
    (content: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        onChange(content);
      }, 300);
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    onChange(editor.root.innerHTML === "<p><br></p>" ? "" : editor.root.innerHTML);
  }, [onChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const html =
      clipboardData.getData("text/html") || clipboardData.getData("text/plain");
    const cleanedHtml = stripAllHtmlAndEncodedTags(html);

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    editor.clipboard.dangerouslyPasteHTML(
      range?.index ?? editor.getLength(),
      cleanedHtml
    );
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return (
    <div
      onPaste={disabled ? undefined : handlePaste}
      onBlur={handleBlur}
      style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? "none" : "auto" }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        formats={formats}
        readOnly={disabled}
        onChange={handleChange}
      />
    </div>
  );
};

export default TextEditor;